/**
 * Leaflet-onderdelen voor geplande punten en tekeningen, gedeeld door de projectkaart en de
 * importdialoog: de basiskaart, een gedraaide afbeelding (een uitgelijnde PDF staat zelden
 * precies noord-boven), de GeoJSON-achtergrond en het icoon van een gepland punt.
 */
import L from 'leaflet';

const MAPTILER_KEY = import.meta.env?.VITE_MAPTILER_KEY || '';
const TILE_WORLD = `https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
const TILE_WORLD_AERIAL = `https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`;
const TILE_BRT = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png';
const ATTR = '&copy; <a href="https://www.maptiler.com/" target="_blank" rel="noopener">MapTiler</a> &copy; OpenStreetMap · PDOK';
const NL_BOUNDS = [[50.5, 3.0], [53.8, 7.5]];

/** Basiskaart ('map' | 'aerial') als lijst tegellagen; zelfde opbouw als de kaart-tab. */
export function baseTiles() {
  return {
    map: [
      L.tileLayer(TILE_WORLD, { maxZoom: 22, attribution: ATTR }),
      L.tileLayer(TILE_BRT, { bounds: L.latLngBounds(NL_BOUNDS), maxZoom: 22, maxNativeZoom: 19 }),
    ],
    aerial: [L.tileLayer(TILE_WORLD_AERIAL, { maxZoom: 22, maxNativeZoom: 20, attribution: ATTR })],
  };
}

/**
 * Afbeelding tussen drie hoekpunten (linksboven, rechtsboven, linksonder), ook gedraaid of
 * scheef. Leaflets eigen ImageOverlay kan alleen een rechthoek noord-boven. We zetten een CSS-
 * matrix op het <img>; bij elke zoom/verschuiving opnieuw uit de drie hoekpunten berekend.
 *
 * Hoekpunten als [lon, lat] (GeoJSON-volgorde, zoals de backend ze opslaat).
 */
export const RotatedImageOverlay = L.Layer.extend({
  options: { opacity: 0.85, pane: 'overlayPane', interactive: false },

  initialize(url, corners, options) {
    this._url = url;
    this.setCorners(corners);
    L.setOptions(this, options);
  },

  setCorners(corners) {
    this._tl = L.latLng(corners.topLeft[1], corners.topLeft[0]);
    this._tr = L.latLng(corners.topRight[1], corners.topRight[0]);
    this._bl = L.latLng(corners.bottomLeft[1], corners.bottomLeft[0]);
    if (this._map) this._reset();
    return this;
  },

  setOpacity(o) {
    this.options.opacity = o;
    if (this._img) this._img.style.opacity = o;
    return this;
  },

  getBounds() {
    const br = L.latLng(this._tr.lat + this._bl.lat - this._tl.lat, this._tr.lng + this._bl.lng - this._tl.lng);
    return L.latLngBounds([this._tl, this._tr, this._bl, br]);
  },

  onAdd(map) {
    if (!this._img) {
      const img = L.DomUtil.create('img', 'leaflet-image-layer leaflet-zoom-animated');
      img.style.transformOrigin = '0 0';
      img.style.opacity = this.options.opacity;
      img.style.position = 'absolute';
      img.style.left = '0';
      img.style.top = '0';
      img.style.maxWidth = 'none';
      img.draggable = false;
      img.onload = () => this._reset();
      img.src = this._url;
      this._img = img;
    }
    this.getPane().appendChild(this._img);
    this._reset();
  },

  onRemove() {
    L.DomUtil.remove(this._img);
  },

  getEvents() {
    return { zoom: this._reset, viewreset: this._reset, zoomanim: this._animateZoom };
  },

  _apply(tl, tr, bl) {
    const img = this._img;
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return;
    const a = (tr.x - tl.x) / w, b = (tr.y - tl.y) / w;
    const c = (bl.x - tl.x) / h, d = (bl.y - tl.y) / h;
    img.style.width = `${w}px`;
    img.style.height = `${h}px`;
    img.style.transform = `matrix(${a},${b},${c},${d},${tl.x},${tl.y})`;
  },

  _reset() {
    if (!this._map || !this._img) return;
    const m = this._map;
    this._apply(m.latLngToLayerPoint(this._tl), m.latLngToLayerPoint(this._tr), m.latLngToLayerPoint(this._bl));
  },

  _animateZoom(e) {
    const m = this._map;
    const p = (ll) => m._latLngToNewLayerPoint(ll, e.zoom, e.center);
    this._apply(p(this._tl), p(this._tr), p(this._bl));
  },
});

/** Achtergrondtekening (GeoJSON): dunne lijnen, teksten als kleine labels vanaf zoom 18. */
export function vectorLayer(geojson, { color = '#7c3aed', weight = 1.2, opacity = 0.9 } = {}) {
  const group = L.featureGroup();
  const lines = L.geoJSON(geojson, {
    filter: (f) => !f.properties?.text,
    style: () => ({ color, weight, opacity, fill: false }),
    pointToLayer: (f, ll) => L.circleMarker(ll, { radius: 3, color, weight: 1, fillOpacity: 0.6 }),
  });
  group.addLayer(lines);
  const labels = L.layerGroup();
  for (const f of geojson.features || []) {
    if (!f.properties?.text || f.geometry?.type !== 'Point') continue;
    const [lng, lat] = f.geometry.coordinates;
    labels.addLayer(L.marker([lat, lng], {
      interactive: false,
      icon: L.divIcon({
        className: 'mv-dxf-label',
        html: `<span style="font:600 10px/1 system-ui,sans-serif;color:${color};white-space:nowrap;text-shadow:0 0 2px #fff,0 0 2px #fff">${escapeHtml(f.properties.text)}</span>`,
        iconSize: null,
      }),
    }));
  }
  group.on('add', () => {
    const map = group._map;
    const sync = () => {
      if (!group._map) return;
      if (map.getZoom() >= 18) { if (!group.hasLayer(labels)) group.addLayer(labels); }
      else if (group.hasLayer(labels)) group.removeLayer(labels);
    };
    group._labelSync = sync;
    map.on('zoomend', sync);
    sync();
  });
  group.on('remove', () => { if (group._labelSync) group._map?.off('zoomend', group._labelSync); });
  return group;
}

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Kleur per soort, zodat peilbuizen en proefgaten er op het eerste gezicht uitspringen. */
export const PLANNED_KIND_COLORS = {
  boring: '#ea580c',
  peilbuis: '#0284c7',
  proefgat: '#a16207',
  sleuf: '#a16207',
  overig: '#64748b',
};

/**
 * Icoon van een gepland punt: open ring met naam. Uitgevoerd = gevuld groen met vinkje, zodat
 * je in één oogopslag ziet wat er nog moet.
 */
export function plannedPointIcon(p, { selected = false, showLabel = true } = {}) {
  const done = p.status === 'done';
  const color = done ? '#2e9e5b' : (PLANNED_KIND_COLORS[p.kind] || PLANNED_KIND_COLORS.boring);
  const size = selected ? 20 : 14;
  const square = p.kind === 'proefgat' || p.kind === 'sleuf';
  const shape = `width:${size}px;height:${size}px;box-sizing:border-box;border:2.5px solid ${color};` +
    `border-radius:${square ? '3px' : '50%'};background:${done ? color : 'rgba(255,255,255,.85)'};` +
    `box-shadow:0 1px 4px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;font:700 9px/1 system-ui`;
  // Gekozen: een blauwe ring eromheen en het label als blauw kaartje, zodat je hem tussen de
  // andere punten meteen terugvindt. De ring pulseert waar de pagina de animatie 'mv-halo' kent.
  const label = !showLabel ? ''
    : selected
      ? `<span style="position:absolute;left:${size + 6}px;top:50%;transform:translateY(-50%);font:700 11px/1 system-ui,sans-serif;color:#fff;background:#2563eb;padding:3px 6px;border-radius:5px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3)">${escapeHtml(p.name)}</span>`
      : `<span style="position:absolute;left:${size + 3}px;top:50%;transform:translateY(-50%);font:700 11px/1 system-ui,sans-serif;color:${done ? '#1f6b3f' : '#1f2933'};white-space:nowrap;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff">${escapeHtml(p.name)}</span>`;
  const halo = selected
    ? `<div class="mv-halo" style="position:absolute;left:50%;top:50%;width:${size + 18}px;height:${size + 18}px;margin:${-(size + 18) / 2}px 0 0 ${-(size + 18) / 2}px;border-radius:50%;border:2.5px solid #2563eb;background:rgba(37,99,235,.18);box-sizing:border-box;pointer-events:none"></div>`
    : '';
  return L.divIcon({
    className: 'mv-planned',
    html: `<div style="position:relative;width:${size}px;height:${size}px">${halo}<div style="position:relative;${shape}">${done ? '✓' : ''}</div>${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// ── Doorzichtigheid van een tekening ─────────────────────────────────────────

/**
 * Hoe dekkend een tekening is (0,1–1). Een PDF/afbeelding bewaart dat in `georef.opacity`, een
 * CAD/GIS-tekening in `style.opacity`.
 */
export function layerOpacity(l) {
  const v = l?.kind === 'image' ? l?.georef?.opacity : l?.style?.opacity;
  return Number.isFinite(Number(v)) && v != null ? Number(v) : (l?.kind === 'image' ? 0.85 : 0.9);
}

/** Wat er naar de API gaat om de doorzichtigheid te bewaren. */
export function opacityPatch(l, opacity) {
  const o = Math.min(1, Math.max(0.1, Number(opacity)));
  return l.kind === 'image' ? { georef: { ...(l.georef || {}), opacity: o } } : { style: { ...(l.style || {}), opacity: o } };
}

/** Meteen op de kaart toepassen (tijdens het schuiven, nog zonder op te slaan). */
export function applyOpacity(obj, l, opacity) {
  if (!obj) return;
  if (l.kind === 'image') obj.setOpacity?.(opacity);
  else obj.eachLayer?.((sub) => sub.setStyle?.({ opacity }));
}
